import React, { useState, useEffect, useCallback } from 'react';
import { X, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';

const TOUR_STEPS = [
  {
    target: '[data-testid="upload-dropzone"]',
    title: 'Upload Your Model',
    body: 'Drag & drop any STL file here. Analysis starts automatically — no extra clicks needed.',
    position: 'bottom',
  },
  {
    target: '[data-testid="unified-workflow"] .flex.items-center.gap-0',
    title: 'Track Your Progress',
    body: 'This bar shows where you are: Upload, Analyze, Repair (if needed), then Generate supports.',
    position: 'bottom',
  },
  {
    target: '[data-testid="unified-workflow"]',
    title: 'Generate & Export',
    body: 'After analysis, configure tree style and density, then download STL/3MF or export a professional PDF quote.',
    position: 'center',
  },
];

const STORAGE_KEY = 'printmind_tour_seen';

const OnboardingTour = ({ show }) => {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!show) return;
    const seen = localStorage.getItem(STORAGE_KEY);
    if (!seen) {
      const timer = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(timer);
    }
  }, [show]);

  const updateRect = useCallback(() => {
    const s = TOUR_STEPS[step];
    const el = document.querySelector(s.target);
    if (el) {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    } else {
      setRect(null);
    }
  }, [step]);

  useEffect(() => {
    if (!visible) return;
    updateRect();
    window.addEventListener('resize', updateRect);
    return () => window.removeEventListener('resize', updateRect);
  }, [visible, step, updateRect]);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  const next = () => {
    if (step < TOUR_STEPS.length - 1) setStep(step + 1);
    else dismiss();
  };

  const prev = () => {
    if (step > 0) setStep(step - 1);
  };

  if (!visible) return null;

  const current = TOUR_STEPS[step];
  const isCenter = current.position === 'center' || !rect;

  // Tooltip positioning
  let tooltipStyle = {};
  if (rect && !isCenter) {
    tooltipStyle = {
      position: 'fixed',
      top: rect.top + rect.height + 16,
      left: Math.max(16, rect.left + rect.width / 2 - 180),
      zIndex: 10001,
    };
  }

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 z-[9999] bg-black/60 transition-opacity duration-300"
        onClick={dismiss}
        data-testid="tour-backdrop"
      />

      {/* Highlight cutout */}
      {rect && !isCenter && (
        <div
          className="fixed z-[10000] rounded-xl ring-4 ring-primary/60 pointer-events-none"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
          }}
        />
      )}

      {/* Tooltip */}
      <div
        className={`${
          isCenter
            ? 'fixed inset-0 z-[10001] flex items-center justify-center p-4'
            : ''
        }`}
        data-testid="tour-tooltip"
      >
        <div
          className="bg-bg-light border border-primary/30 rounded-2xl shadow-2xl shadow-primary/10 w-[360px] p-5 space-y-3"
          style={isCenter ? { zIndex: 10001 } : tooltipStyle}
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-xs text-primary font-semibold uppercase tracking-wide">
                Step {step + 1} of {TOUR_STEPS.length}
              </span>
            </div>
            <button
              onClick={dismiss}
              className="text-text-muted hover:text-text-primary transition"
              data-testid="tour-dismiss-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <h3 className="text-base font-bold text-text-primary">{current.title}</h3>
          <p className="text-sm text-text-secondary leading-relaxed">{current.body}</p>

          {/* Progress dots */}
          <div className="flex items-center gap-1.5 pt-1">
            {TOUR_STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? 'w-6 bg-primary' : 'w-1.5 bg-text-muted/30'
                }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-1">
            <button
              onClick={dismiss}
              className="text-xs text-text-muted hover:text-text-secondary transition"
              data-testid="tour-skip-btn"
            >
              Skip tour
            </button>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <button
                  onClick={prev}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary border border-text-muted/20 rounded-lg transition"
                  data-testid="tour-prev-btn"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back
                </button>
              )}
              <button
                onClick={next}
                className="flex items-center gap-1 px-4 py-1.5 text-sm bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition"
                data-testid="tour-next-btn"
              >
                {step === TOUR_STEPS.length - 1 ? 'Get Started' : 'Next'}
                {step < TOUR_STEPS.length - 1 && <ArrowRight className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingTour;
